import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {MilestoneCreate} from '@github-ui/repository-milestone/MilestoneCreate'
import {useEffect, useEffectEvent} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {renderIssuesSidebar} from '../../components/sidebar/render-issues-sidebar'
import {useSetCurrentViewId} from '../../contexts/QueryContext'
import {useEntryPointsLoader} from '../../hooks/use-entrypoint-loaders'
import {IssueIndexPage, RepositoryMilestonePage} from '../../lazy-routes'
import {preloadWhenIdle} from '../../utils/preload-when-idle'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import type {RepositoryMilestoneNewPageContentInternal$key} from './__generated__/RepositoryMilestoneNewPageContentInternal.graphql'
import type {RepositoryMilestoneNewPageQuery} from './__generated__/RepositoryMilestoneNewPageQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestoneNewPageQuery($name: String!, $owner: String!) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...RepositoryMilestoneNewPageContentInternal
    }
  }
`

export const RepositoryMilestoneNewPage: EntryPointComponent<
  {pageQuery: RepositoryMilestoneNewPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone New">
      <RepositoryMilestoneNewPageContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryMilestoneNewPageContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestoneNewPageQuery>
}) {
  const pageData = usePreloadedQuery<RepositoryMilestoneNewPageQuery>(PageQuery, pageQueryRef)
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')

  const schedulePreloads = useEffectEvent(() => {
    return preloadWhenIdle(() => {
      RepositoryMilestonePage.preload()
      // When issue_pinned_views is on, the sidebar hook covers IssueIndexPage.
      // Remove this line when the flag is cleaned up.
      if (!pinnedViewsEnabled) {
        IssueIndexPage.preload()
      }
    })
  })
  useEffect(() => {
    // pinnedViewsEnabled is read once at mount; flag flips during a session are
    // not expected to retroactively change the preload set.
    return schedulePreloads()
  }, [])

  const setCurrentViewId = useSetCurrentViewId()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryMilestoneNewPageContentInternal repository={okResult.value} />
}

function RepositoryMilestoneNewPageContentInternal({
  repository,
}: {
  repository: RepositoryMilestoneNewPageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryMilestoneNewPageContentInternal on Repository {
        id
        nameWithOwner
        viewerCanPush
      }
    `,
    repository,
  )

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  return (
    <RepoIssuesPageLayout
      dialogTitle="Milestones"
      title={sidebarLayoutEnabled ? 'Create milestone' : undefined}
      renderSidebar={renderIssuesSidebar}
    >
      <MilestoneCreate repository={data} />
    </RepoIssuesPageLayout>
  )
}
